import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ETATS_INSTRUCTION, AVIS_INVESTISSEMENT } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
import { FinancementsInstruction } from './FinancementsInstruction';

interface Dossier {
  id: string;
  version: number;
  statut: keyof typeof ETATS_INSTRUCTION;
  dossier: {
    instruction_precedente_id?: string | null;
    titre: string;
    justification: string;
    montant: string;
    organisation: string;
    fictive: boolean;
    pieces?: { id: string; nom: string; taille: number }[];
  };
  avis: keyof typeof AVIS_INVESTISSEMENT | null;
  motif: string | null;
  instruit_par: string | null;
  transmis_par: string | null;
  valide_le: string | null;
}
function Historique({ id, contexte }: { id: string; contexte: string }) {
  const q = useQuery({
    queryKey: ['instruction-investissements', contexte, id],
    queryFn: () =>
      requete<{
        elements: {
          action: string;
          statut: keyof typeof ETATS_INSTRUCTION;
          cree_le: string;
          motif: string;
        }[];
      }>(`/instruction-investissements/${id}/historique`),
  });
  return (
    <>
      {q.isPending && <p>Chargement du suivi…</p>}
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      <ol>
        {q.data?.elements.map((h, i) => (
          <li key={i}>
            {new Date(h.cree_le).toLocaleString('fr-FR')} · {ETATS_INSTRUCTION[h.statut]}{' '}
            {h.motif && `· ${h.motif}`}
          </li>
        ))}
      </ol>
    </>
  );
}
export function PageInstructionInvestissements() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [historique, setHistorique] = useState<string | null>(null);
  const appartenance = contexte?.appartenance_active?.id ?? '';
  const q = useQuery({
    queryKey: ['instruction-investissements', appartenance],
    queryFn: () =>
      requete<{
        elements: Dossier[];
        tronque: boolean;
        peut_instruire: boolean;
        peut_valider: boolean;
      }>('/instruction-investissements'),
    enabled: peut(contexte, 'investissement', 'voir'),
  });
  async function telecharger(id: string, piece: { id: string; nom: string }) {
    setErreur('');
    setOccupe(true);
    try {
      const r = await requete<Response>(`/instruction-investissements/${id}/pieces/${piece.id}`, {
        brut: true,
      });
      const url = URL.createObjectURL(await r.blob());
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = piece.nom;
      lien.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  async function envoyer(d: Dossier, f: FormData) {
    setErreur('');
    setOccupe(true);
    try {
      await requete(`/instruction-investissements/${d.id}/actions`, {
        methode: 'POST',
        corps: {
          action: f.get('action'),
          version: d.version,
          motif: f.get('motif'),
          ...(f.get('action') === 'proposer' ? { avis: f.get('avis') } : {}),
        },
      });
      await client.invalidateQueries({ queryKey: ['instruction-investissements'] });
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  if (!peut(contexte, 'investissement', 'voir'))
    return <p role="alert">Accès au suivi des investissements non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Instruction des investissements" />
      <p>
        Suivez les dossiers transmis et leur avis institutionnel. Un avis validé ne constitue pas
        une attribution de financement ni un ordre de paiement.
      </p>
      {erreur && <p role="alert">{erreur}</p>}
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      {q.isPending && <p>Chargement des dossiers…</p>}
      {q.data?.tronque && (
        <p role="status">Seuls les 500 dossiers les plus récents sont affichés.</p>
      )}
      {q.data?.elements.length === 0 && <p>Aucun dossier transmis dans votre périmètre.</p>}
      {q.data?.elements.map((d) => {
        if (!d.dossier || typeof d.dossier !== 'object' || !/^\d+$/.test(String(d.dossier.montant)))
          return (
            <p key={d.id} role="alert">
              Dossier {d.id} : données invalides. Contactez votre administrateur avant toute
              instruction.
            </p>
          );
        const proposer =
          q.data.peut_instruire &&
          d.statut === 'RECEIVED' &&
          d.transmis_par !== contexte?.compte.id;
        const valider =
          q.data.peut_valider &&
          d.statut === 'PROPOSED' &&
          d.instruit_par !== contexte?.compte.id &&
          d.transmis_par !== contexte?.compte.id;
        return (
          <article
            id={d.id}
            key={d.id}
            style={{ paddingBlock: 20, borderBottom: '1px solid var(--bordure, currentColor)' }}
          >
            <h2>{d.dossier.titre}</h2>
            {d.dossier.instruction_precedente_id && (
              <p>
                Nouvelle version de complément ·{' '}
                <a href={`#${d.dossier.instruction_precedente_id}`}>Consulter l’avis précédent</a>
              </p>
            )}
            <p>
              {d.dossier.organisation}
              {d.dossier.fictive ? ' · Démonstration fictive' : ''}
            </p>
            <p>
              <strong>{ETATS_INSTRUCTION[d.statut]}</strong> ·{' '}
              {BigInt(d.dossier.montant).toLocaleString('fr-FR')} XAF
            </p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{d.dossier.justification}</p>
            <details>
              <summary>Justificatifs transmis ({d.dossier.pieces?.length ?? 0})</summary>
              <p>Pièces figées lors de la transmission du dossier.</p>
              {!d.dossier.pieces?.length && <p>Aucun justificatif transmis.</p>}
              <ul>
                {d.dossier.pieces?.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      disabled={occupe}
                      style={{ maxWidth: '100%', overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                      onClick={() => void telecharger(d.id, p)}
                    >
                      Télécharger {p.nom}
                    </button>{' '}
                    — {Math.ceil(p.taille / 1024)} Kio
                  </li>
                ))}
              </ul>
            </details>
            {d.avis && (
              <p>
                <strong>{AVIS_INVESTISSEMENT[d.avis]}</strong>
                {d.statut !== 'VALIDATED' ? ' · Proposition en attente de contrôle' : ''}
              </p>
            )}
            {d.motif && <p style={{ whiteSpace: 'pre-wrap' }}>{d.motif}</p>}
            {d.valide_le && <p>Avis validé le {new Date(d.valide_le).toLocaleString('fr-FR')}</p>}
            {d.statut === 'VALIDATED' && d.avis === 'FAVORABLE' && (
              <FinancementsInstruction
                id={d.id}
                preparer={q.data.peut_instruire && d.transmis_par !== contexte?.compte.id}
                controler={q.data.peut_valider && d.transmis_par !== contexte?.compte.id}
              />
            )}
            {(proposer || valider) && (
              <form
                key={d.version}
                style={{ display: 'grid', gap: 12, maxWidth: 750 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void envoyer(d, new FormData(e.currentTarget));
                }}
              >
                <h3>{proposer ? 'Proposer un avis' : 'Contrôler la proposition'}</h3>
                {proposer ? (
                  <>
                    <input type="hidden" name="action" value="proposer" />
                    <label>
                      Avis proposé{' '}
                      <select name="avis">
                        {Object.entries(AVIS_INVESTISSEMENT).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                ) : (
                  <label>
                    Décision de contrôle{' '}
                    <select name="action">
                      <option value="valider">Valider cet avis</option>
                      <option value="retourner">Retourner pour correction</option>
                    </select>
                  </label>
                )}
                <label>
                  Motivation détaillée{' '}
                  <textarea name="motif" required minLength={20} maxLength={4000} rows={4} />
                </label>
                <button disabled={occupe}>
                  {proposer ? 'Soumettre la proposition' : 'Enregistrer le contrôle'}
                </button>
              </form>
            )}
            <button
              aria-expanded={historique === d.id}
              onClick={() => setHistorique(historique === d.id ? null : d.id)}
            >
              Historique du suivi
            </button>
            {historique === d.id && <Historique id={d.id} contexte={appartenance} />}
          </article>
        );
      })}
    </section>
  );
}
